import { useMemo } from "react";
import { Box, Card, Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { TypeLabel } from "../TagIcon";
import { CardMedia } from "./CardMedia";

// Warm card with a full-height media region covering the left 30%. The
// region is always present: sources are tried in order (e.g. experience
// photo, then the item's default image), falling back to a placeholder.
// `imageUrl`/`imageAlt` remain as shorthand for a single source.
export const ItemCard = ({ imageSources, imageUrl, imageAlt = '', mediaResetKey, onImageFailed, children, component = 'article', sx = {}, contentSx = {} }) => {
    const sources = useMemo(
        () => imageSources || (imageUrl ? [{ url: imageUrl, alt: imageAlt }] : []),
        [imageSources, imageUrl, imageAlt],
    );

    return (
        <Card
            component={component}
            sx={{
                display: 'grid',
                gridTemplateColumns: 'var(--photo-width, 30%) minmax(0, 1fr)',
                overflow: 'hidden',
                ...sx,
            }}
        >
            <CardMedia sources={sources} resetKey={mediaResetKey} onSourceFailed={onImageFailed} />
            <Box sx={{ p: { xs: '17px', sm: '22px' }, minWidth: 0, ...contentSx }}>
                {children}
            </Box>
        </Card>
    );
}

// Sage rule beside the item identity: type icon, category label, title, badges.
export const ItemIdentity = ({ item, titleComponent = 'h2', titleVariant = 'h2', link = true, children, sx = {} }) => {
    const title = item?.name || 'Untitled';
    return (
        <Box sx={{ borderLeft: '3px solid', borderColor: 'sage.main', pl: { xs: '10px', sm: '14px' }, minWidth: 0, ...sx }}>
            <TypeLabel name={item?.tag?.name} />
            <Typography component={titleComponent} variant={titleVariant} sx={{ my: '6px', overflowWrap: 'anywhere' }}>
                {link && item?.id ?
                    <Link component={RouterLink} to={`/items/${item.id}`} color="inherit">{title}</Link>
                    : title
                }
            </Typography>
            {children ?
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '6px', mt: '8px' }}>
                    {children}
                </Box>
            : null}
        </Box>
    );
}

export const StatusBadge = ({ children, sx = {}, ...props }) => {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                bgcolor: 'sage.surface',
                color: 'sage.text',
                borderRadius: '5px',
                fontSize: '0.75rem',
                lineHeight: 1.5,
                px: '8px',
                py: '3px',
                overflowWrap: 'anywhere',
                ...sx,
            }}
            {...props}
        >
            {children}
        </Box>
    );
}

// Sage-tinted panel used for social/aggregate sections.
export const SagePanel = ({ children, sx = {}, ...props }) => {
    return (
        <Box
            component="section"
            sx={{ bgcolor: 'sage.surface', color: 'sage.text', borderRadius: '14px', p: { xs: '20px', sm: '23px' }, ...sx }}
            {...props}
        >
            {children}
        </Box>
    );
}

export const SectionHeading = ({ children, sub, component = 'h2', sx = {} }) => {
    return (
        <Box sx={{ mb: '12px', ...sx }}>
            <Typography component={component} variant="h2">{children}</Typography>
            {sub ? <Typography variant="body2" color="text.secondary">{sub}</Typography> : null}
        </Box>
    );
}
